/**
 * Date Formatting Utilities
 *
 * Formats dates in Japanese calendar era format to match RA2 system
 * Example: 2026-01-16 -> 令和08年01月16日
 */

/**
 * Japanese Era Definition
 */
interface JapaneseEra {
  name: string;
  startDate: Date;
  startYear: number;
}

/**
 * Japanese Eras (from newest to oldest)
 */
const JAPANESE_ERAS: JapaneseEra[] = [
  { name: '令和', startDate: new Date(2019, 4, 1), startYear: 2019 },   // Reiwa: 2019-05-01
  { name: '平成', startDate: new Date(1989, 0, 8), startYear: 1989 },   // Heisei: 1989-01-08
  { name: '昭和', startDate: new Date(1926, 11, 25), startYear: 1926 }, // Showa: 1926-12-25
  { name: '大正', startDate: new Date(1912, 6, 30), startYear: 1912 },  // Taisho: 1912-07-30
  { name: '明治', startDate: new Date(1868, 0, 25), startYear: 1868 },  // Meiji: 1868-01-25
];

export function formatDateYmd(date: Date, separator = '-'): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return [year, month, day].join(separator);
}

/**
 * Get Japanese era for a given date
 */
function getJapaneseEra(date: Date): { era: JapaneseEra; year: number } {
  for (const era of JAPANESE_ERAS) {
    if (date >= era.startDate) {
      const year = date.getFullYear() - era.startYear + 1;
      return { era, year };
    }
  }
  // Default to oldest era for very old dates
  const oldestEra = JAPANESE_ERAS[JAPANESE_ERAS.length - 1];
  return { era: oldestEra, year: date.getFullYear() - oldestEra.startYear + 1 };
}

/**
 * Format date in Japanese calendar era format
 *
 * @param dateStr - Date string in YYYY-MM-DD, ISO datetime format, or Date object
 * @returns Formatted date string like "令和06年01月16日"
 */
export function formatJapaneseDate(dateStr: string | Date): string {
  let date: Date;

  if (typeof dateStr === 'string') {
    // Handle ISO datetime format (2026-01-16T00:00:00.000000Z)
    // Extract just the date part (YYYY-MM-DD)
    const datePart = dateStr.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    date = new Date(year, month - 1, day);
  } else {
    date = dateStr;
  }

  if (isNaN(date.getTime())) {
    return dateStr as string; // Return original if invalid
  }

  const { era, year } = getJapaneseEra(date);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const eraYear = String(year).padStart(2, '0');

  return `${era.name}${eraYear}年${month}月${day}日`;
}

/**
 * Format date in short Japanese format (without era)
 *
 * @param dateStr - Date string in YYYY-MM-DD format
 * @returns Formatted date string like "2026年01月16日"
 */
export function formatJapaneseDateShort(dateStr: string | Date): string {
  let date: Date;

  if (typeof dateStr === 'string') {
    const [year, month, day] = dateStr.split('-').map(Number);
    date = new Date(year, month - 1, day);
  } else {
    date = dateStr;
  }

  if (isNaN(date.getTime())) {
    return dateStr as string;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}年${month}月${day}日`;
}

/**
 * Format ISO datetime string or Date object into yyyy/MM/dd HH:mm
 */
export function formatAuditDateTime(dateStr?: string | Date | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  const yyyy = date.getFullYear();
  const MM = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const HH = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${yyyy}/${MM}/${dd} ${HH}:${mm}`;
}
